import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { clearExpiredDemoSignIn, hasExpiredDemoSignIn } from '@/lib/demoSignInExpiry'
import { ApiError, postJSON } from '@/lib/api'

export interface PlatformSurface {
  brand: string
}

interface CurrentUserResponse {
  user: { email: string }
}

interface SignInForm {
  email: string
  password: string
}

interface PlatformAdminSignInPageProps {
  platform: PlatformSurface
  onSignedIn?: (user: CurrentUserResponse['user']) => void
}

export default function PlatformAdminSignInPage({
  platform,
  onSignedIn,
}: PlatformAdminSignInPageProps) {
  const [form, setForm] = React.useState<SignInForm>({ email: '', password: '' })
  const [message, setMessage] = React.useState<string | null>(() =>
    hasExpiredDemoSignIn() ? 'Link expired' : null,
  )
  const [signedInUser, setSignedInUser] = React.useState<CurrentUserResponse['user'] | null>(null)
  const signIn = useMutation({
    mutationFn: () => postJSON<CurrentUserResponse>('/api/v1/platform/sessions', form),
    onSuccess: (data) => {
      clearExpiredDemoSignIn()
      setMessage(null)
      setSignedInUser(data.user)
      onSignedIn?.(data.user)
    },
    onError: (error: Error) => {
      setMessage(apiMessage(error))
    },
  })

  if (signedInUser) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm">
          <BrandHeader brand={platform.brand} />
          <h1 className="text-heading-sm font-semibold text-foreground">
            Signed in to the control plane
          </h1>
          <p className="pt-1 text-body-sm text-muted-foreground">{signedInUser.email}</p>
        </div>
      </main>
    )
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <BrandHeader brand={platform.brand} />

        <h1 className="text-heading-sm font-semibold text-foreground">Platform admin sign-in</h1>

        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            signIn.mutate()
          }}
        >
          {message && <Alert>{message}</Alert>}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="platform-email">Email</Label>
            <Input
              id="platform-email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              autoComplete="email"
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="platform-password">Password</Label>
            <Input
              id="platform-password"
              type="password"
              placeholder="password"
              value={form.password}
              autoComplete="current-password"
              onChange={(event) => setForm({ ...form, password: event.target.value })}
            />
          </div>
          <Button type="submit" className="mt-1 w-full" disabled={signIn.isPending}>
            {signIn.isPending ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </div>
    </main>
  )
}

function BrandHeader({ brand }: { brand: string }) {
  return (
    <div className="mb-8 flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-body-sm text-primary-foreground">
        {brand.slice(0, 1)}
      </span>
      <span className="text-body-sm text-foreground">{brand}</span>
    </div>
  )
}

function apiMessage(error: Error) {
  if (error instanceof ApiError) return error.message

  return 'Sign-in failed.'
}
